import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { useNavigate } from 'react-router-dom';

export const Navbar = ({ onOpenAuth, user, onLogout, theme, onToggleTheme }) => {
  const { language, setLanguage, t } = useLanguage();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const languages = [
    { code: 'ru', label: 'RU' },
    { code: 'en', label: 'EN' },
    { code: 'zh', label: 'CN' },
    { code: 'uk', label: 'UA' }
  ];

  const handleLangChange = (code) => {
    setLanguage(code);
    setLangDropdownOpen(false);
  };

  const navItems = [
    { id: 'features', label: t('nav.features'), path: '/#features' },
    { id: 'stats', label: t('nav.stats'), path: '/#stats' },
    { id: 'features-mid', label: t('nav.gallery'), path: '/#features-mid' },
    { id: 'pricing', label: t('nav.pricing'), path: '/#pricing' },
    { id: 'about', label: t('nav.about'), path: '/#about' },
    { id: 'faq', label: t('nav.faq'), path: '/#faq' }
  ];

  const scrollToSection = (id) => {
    setMobileMenuOpen(false);
    if (window.location.pathname !== '/') {
      navigate('/#' + id);
      return;
    }
    const element = document.getElementById(id);
    if (element) {
      const offset = 80;
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <nav className={`navbar ${scrolled ? 'scrolled' : ''}`} id="main-navbar">
      <div className="nav-container">
        <div 
          className="nav-brand" 
          onClick={() => { navigate('/'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
          id="nav-logo-brand"
        >
          <div className="nav-logo-icon">
            <i className="fas fa-paper-plane"></i>
          </div>
          <span className="nav-logo-text">Telegram<span>PRIME</span></span>
        </div>

        <ul className="nav-links" id="nav-menu-links">
          {navItems.map((item) => (
            <li key={item.id}>
              <span 
                className="nav-link-item" 
                onClick={() => scrollToSection(item.id)}
                id={`nav-link-${item.id}`}
              >
                {item.label}
              </span>
            </li>
          ))}
        </ul>

        <div className="nav-controls" id="nav-controls-container">
          

          <div className="lang-selector-wrapper">
            <button 
              className="lang-select-btn" 
              onClick={() => setLangDropdownOpen(!langDropdownOpen)}
              id="nav-lang-select-btn"
            >
              <i className="fas fa-globe"></i>
              <span className="lang-label">{languages.find(l => l.code === language)?.label || 'RU'}</span>
              <i className="fas fa-chevron-down dropdown-arrow"></i>
            </button>
            
            {langDropdownOpen && (
              <>
                <div className="dropdown-overlay" onClick={() => setLangDropdownOpen(false)} />
                <ul className="lang-dropdown" id="nav-lang-dropdown">
                  {languages.map((lang) => (
                    <li 
                      key={lang.code} 
                      className={language === lang.code ? 'active' : ''}
                      onClick={() => handleLangChange(lang.code)}
                      id={`lang-option-${lang.code}`}
                    >
                      {lang.label}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>

          {user ? (
            <div className="auth-profile-wrap" id="nav-auth-profile">
              <span className="user-badge" id="nav-user-badge">
                <i className="fas fa-user-circle mr-2"></i>
                {user.username}
              </span>
              <button 
                className="nav-logout-btn" 
                onClick={onLogout}
                id="nav-logout-btn"
              >
                <i className="fas fa-sign-out-alt mr-1"></i>
                {t('nav.logout')}
              </button>
            </div>
          ) : (
            <button 
              className="nav-auth-btn" 
              onClick={onOpenAuth}
              id="nav-auth-btn"
            >
              <i className="fas fa-user-lock mr-2"></i>
              <span className="nav-auth-btn-text">{t('nav.auth')}</span>
            </button>
          )}

          <button 
            className={`mobile-menu-toggle ${mobileMenuOpen ? 'open' : ''}`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            id="mobile-menu-toggle-btn"
            aria-label="Toggle mobile menu"
          >
            <span />
            <span />
            <span />
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="mobile-drawer-overlay" onClick={() => setMobileMenuOpen(false)} />
      )}
      {mobileMenuOpen && (
        <div className="mobile-drawer" id="mobile-navigation-drawer">
          <ul className="mobile-nav-links">
            {navItems.map((item) => (
              <li key={item.id}>
                <span 
                  className="mobile-nav-link-item" 
                  onClick={() => scrollToSection(item.id)}
                  id={`nav-link-${item.id}-mobile`}
                >
                  {item.label}
                </span>
              </li>
            ))}
            {user ? (
              <li className="mobile-auth-li">
                <span className="mobile-user-name">
                  <i className="fas fa-user-circle mr-2"></i>
                  {user.username}
                </span>
                <button 
                  className="nav-logout-btn w-full" 
                  onClick={() => { onLogout(); setMobileMenuOpen(false); }}
                  id="mobile-logout-btn"
                >
                  <i className="fas fa-sign-out-alt mr-1"></i>
                  {t('nav.logout')}
                </button>
              </li>
            ) : (
              <li className="mobile-auth-li">
                <button 
                  className="nav-auth-btn w-full" 
                  onClick={() => { onOpenAuth(); setMobileMenuOpen(false); }}
                  id="mobile-auth-btn"
                >
                  <i className="fas fa-user-lock mr-2"></i>
                  {t('nav.auth')}
                </button>
              </li>
            )}
          </ul>
        </div>
      )}
    </nav>
  );
};

export default Navbar;